<!--
  Copyright (c) 2025 NoorWays. All rights reserved.
  Licensed under the terms specified in the LICENSE.txt file.
-->

<template>
  <div class="noorwriter-logo" :class="logoClasses">
    <img
      :src="logoSrc"
      :alt="altText"
      :width="computedWidth"
      :height="computedHeight"
      class="logo-image"
      @error="handleImageError"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAppStore } from '@/stores/app'

// Import logo assets
import logoStandard from '@/assets/images/noorwriter-logo.svg'
import logoInverted from '@/assets/images/noorwriter-logo-inverted.svg'
import logoWhite from '@/assets/images/noorwriter-logo-white.png'
import symbolLogo from '@/assets/images/noorwriter-symbol.svg'

interface Props {
  variant?: 'main' | 'symbol'
  size?: 'small' | 'medium' | 'large' | 'xlarge'
  theme?: 'auto' | 'light' | 'dark' | 'white'
  width?: number
  height?: number
  clickable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'main',
  size: 'medium',
  theme: 'auto',
  clickable: false,
})

const { t } = useI18n()
const appStore = useAppStore()
const imageError = ref(false)

// Size configurations
const sizeConfig = {
  small: { width: 120, height: 40 },
  medium: { width: 180, height: 60 },
  large: { width: 240, height: 80 },
  xlarge: { width: 320, height: 107 },
}

// Computed properties
const logoSrc = computed(() => {
  if (imageError.value) {
    return '' // Fallback to empty if image fails to load
  }

  if (props.variant === 'symbol') {
    return symbolLogo
  }

  // For main logo, check theme with priority order:
  // 1. Explicit theme prop (overrides everything)
  // 2. App store theme (user's stored preference)
  // 3. Fallback to standard logo if store is not available
  if (props.theme === 'white') {
    return logoWhite // White logo for dark gradient backgrounds
  } else if (props.theme === 'dark') {
    return logoInverted // Inverted logo (light text) for dark backgrounds
  } else if (props.theme === 'light') {
    return logoStandard // Standard logo (dark text) for light backgrounds
  } else {
    // Auto theme detection - use app store theme (reflects user's preference)
    const currentTheme = appStore.theme
    return currentTheme === 'dark' ? logoInverted : logoStandard
  }
})

const computedWidth = computed(() => {
  return props.width || sizeConfig[props.size].width
})

const computedHeight = computed(() => {
  return props.height || sizeConfig[props.size].height
})

const altText = computed(() => {
  return props.variant === 'symbol'
    ? t('app.logoAlt', { name: 'n.writer' })
    : t('app.logoAlt', { name: 'n.writer' })
})

const logoClasses = computed(() => ({
  [`logo-${props.size}`]: true,
  [`logo-${props.variant}`]: true,
  [`logo-theme-${props.theme}`]: props.theme !== 'auto',
  'logo-clickable': props.clickable,
  'logo-error': imageError.value,
}))

// Handle image loading errors
function handleImageError() {
  imageError.value = true
}
</script>

<style lang="scss" scoped>
.noorwriter-logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &.logo-clickable {
    cursor: pointer;
    transition: transform 0.2s ease;

    &:hover {
      transform: scale(1.05);
    }
  }

  &.logo-error {
    // Fallback styling when image fails to load
    background: var(--nw-primary);
    color: var(--nw-white);
    border-radius: var(--nw-radius-md);
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;

    &::after {
      content: 'n.writer';
      font-size: 1rem;
    }
  }
}

.logo-image {
  max-width: 100%;
  height: auto;
  object-fit: contain;
  transition: opacity 0.3s ease;

  // Responsive behavior
  @media (max-width: 768px) {
    max-width: 90%;
  }

  @media (max-width: 480px) {
    max-width: 80%;
  }
}

// Size-specific adjustments
.logo-small .logo-image {
  @media (max-width: 480px) {
    max-width: 100px;
  }
}

.logo-medium .logo-image {
  @media (max-width: 768px) {
    max-width: 150px;
  }

  @media (max-width: 480px) {
    max-width: 120px;
  }
}

.logo-large .logo-image {
  @media (max-width: 768px) {
    max-width: 200px;
  }

  @media (max-width: 480px) {
    max-width: 160px;
  }
}

.logo-xlarge .logo-image {
  @media (max-width: 768px) {
    max-width: 250px;
  }

  @media (max-width: 480px) {
    max-width: 200px;
  }
}
</style>
